import Badge from '@/components/ui/Badge';
import type { PlanSportVolume } from '@/api/plansService';

const Stepper = ({
  value,
  min,
  max,
  onChange,
}: {
  value: number;
  min: number;
  max: number;
  onChange: (next: number) => void;
}) => (
  <div className="inline-flex items-center rounded-lg border border-gray-200 dark:border-gray-800">
    <button
      type="button"
      className="px-2 py-1 text-gray-500 disabled:opacity-30"
      disabled={value <= min}
      onClick={() => onChange(value - 1)}
    >
      −
    </button>
    <span className="w-8 text-center text-sm tabular-nums">{value}</span>
    <button
      type="button"
      className="px-2 py-1 text-gray-500 disabled:opacity-30"
      disabled={value >= max}
      onClick={() => onChange(value + 1)}
    >
      +
    </button>
  </div>
);

/** PRD 2 §5.1: sessions-per-week bounds, one row per non-PPG sport of the plan's cycles. */
const SportVolumesTable = ({
  sports,
  volumes,
  labels,
  onChange,
}: {
  sports: string[];
  volumes: Record<string, PlanSportVolume>;
  labels: Record<string, string>;
  onChange: (sportId: string, next: PlanSportVolume) => void;
}) => {
  if (sports.length === 0) {
    return <p className="text-xs text-gray-400">Ajoutez des cycles pour définir les séances par semaine.</p>;
  }
  return (
    <div className="flex flex-col gap-2">
      <table className="text-sm">
        <thead>
          <tr className="text-left text-xs uppercase text-gray-400">
            <th className="pr-6 font-medium">Discipline</th>
            <th className="pr-6 font-medium">Min</th>
            <th className="pr-6 font-medium">Max</th>
            <th className="font-medium">Par défaut</th>
          </tr>
        </thead>
        <tbody>
          {sports.map((sportId) => {
            const v = volumes[sportId] ?? { sportId, min: 1, max: 3, default: 2 };
            const set = (patch: Partial<PlanSportVolume>) => {
              const next = { ...v, ...patch };
              next.max = Math.max(1, Math.min(7, next.max));
              next.min = Math.max(0, Math.min(next.max, next.min));
              next.default = Math.max(next.min, Math.min(next.max, next.default));
              onChange(sportId, next);
            };
            return (
              <tr key={sportId} className="border-t border-gray-100 dark:border-gray-800">
                <td className="py-2 pr-6">
                  <span className="text-gray-700 dark:text-gray-200">{labels[sportId] ?? sportId}</span>
                  {v.min === 0 && (
                    <Badge className="ml-2" color="gray">
                      facultative
                    </Badge>
                  )}
                </td>
                <td className="py-2 pr-6">
                  <Stepper value={v.min} min={0} max={v.default} onChange={(min) => set({ min })} />
                </td>
                <td className="py-2 pr-6">
                  <Stepper value={v.max} min={Math.max(1, v.default)} max={7} onChange={(max) => set({ max })} />
                </td>
                <td className="py-2">
                  <Stepper value={v.default} min={v.min} max={v.max} onChange={(d) => set({ default: d })} />
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
      <p className="text-xs text-gray-400">
        L&apos;athlète choisit son nombre de séances entre le min et le max. Le défaut est proposé. Min 0 : discipline facultative.
      </p>
    </div>
  );
};

export default SportVolumesTable;
